import concat from 'lodash/concat';
import reduce from 'lodash/reduce';

import {
  SECTION_TYPE_NAMES,

  SECTION_TYPE_TILES,
  SECTION_TYPE_TEXT,
  SECTION_TYPE_SLIDESHOW,
  SECTION_TYPE_VIDEOPLAYLIST,
  SECTION_TYPE_PDF,
  SECTION_TYPE_PRESENTATION,
  SECTION_TYPE_IFRAME_EMBED,
  SECTION_TYPE_HTML_PACKAGE,
  SECTION_TYPE_ACCORDION,
  SECTION_TYPE_TABS,
  DO_NOT_SHOW_IN_TABS
} from 'zoomforth_codesbane_viewer/const/sections.js';

import {
  BLOCK_TYPE_CONTAINER,

  BLOCK_SUBTYPE_GRID,
  BLOCK_SUBTYPE_GRID_BIO_ROUNDED,
  BLOCK_SUBTYPE_GRID_BIO_ROUNDED_SINGLE,
  BLOCK_SUBTYPE_GRID_BIO_STANDARD,
  BLOCK_SUBTYPE_GRID_BIO_STANDARD_SINGLE,

  BLOCK_SUBTYPE_GRID_IMAGE_AND_TEXT_FULL_BLEED,
  BLOCK_SUBTYPE_GRID_IMAGE_AND_TEXT_BORDERED,
  BLOCK_SUBTYPE_GRID_VIDEO_AND_TEXT_FULL_BLEED,
  BLOCK_SUBTYPE_GRID_VIDEO_AND_TEXT_BORDERED,

  CATEGORY_TAG_BLANK,
  CATEGORY_TAG_TEXT,
  CATEGORY_TAG_PRESENTATION,
  CATEGORY_TAG_VIDEO,
  CATEGORY_TAG_FILES,
  CATEGORY_TAG_EMBEDS,
  CATEGORY_TAG_IMAGE,
  CATEGORY_TAG_GRID,
  CATEGORY_TAG_IMAGE_BIO,
  CATEGORY_TAG_TABS
} from 'const_shared/blocks/block_definitions.js';


const categoryTagsBySectionType = Object.freeze({
  [SECTION_TYPE_TILES]: [CATEGORY_TAG_GRID],
  [SECTION_TYPE_TEXT]: [CATEGORY_TAG_BLANK, CATEGORY_TAG_TEXT],
  [SECTION_TYPE_SLIDESHOW]: [CATEGORY_TAG_PRESENTATION],
  [SECTION_TYPE_VIDEOPLAYLIST]: [CATEGORY_TAG_VIDEO],
  [SECTION_TYPE_PDF]: [CATEGORY_TAG_FILES],
  [SECTION_TYPE_PRESENTATION]: [CATEGORY_TAG_PRESENTATION],
  [SECTION_TYPE_IFRAME_EMBED]: [CATEGORY_TAG_EMBEDS],
  [SECTION_TYPE_HTML_PACKAGE]: [CATEGORY_TAG_EMBEDS],
  [SECTION_TYPE_ACCORDION]: [CATEGORY_TAG_TEXT],
  [SECTION_TYPE_TABS]: [CATEGORY_TAG_TABS]
});

const SECTION_TYPE_NAMES_TO_SECTION_DESCRIPTION_MAP = Object.freeze({
  [SECTION_TYPE_TILES]: 'Grid',
  [SECTION_TYPE_TEXT]: 'Text',
  [SECTION_TYPE_IFRAME_EMBED]: 'Embed',
  [SECTION_TYPE_PRESENTATION]: 'Presentation',
  [SECTION_TYPE_HTML_PACKAGE]: 'HTML package',
  [SECTION_TYPE_PDF]: 'PDF',
  [SECTION_TYPE_VIDEOPLAYLIST]: 'Video playlist',
  [SECTION_TYPE_SLIDESHOW]: 'Slideshow',
  [SECTION_TYPE_ACCORDION]: 'Accordion',
  [SECTION_TYPE_TABS]: 'Tabs'
});

const newBlocksContainers = Object.freeze([
  /*
    These block subtypes are somewhat misleading.

    In reality, the intention is that we render
    the actual grid layout inside the selector
    modal. This removes the need to switch case
    on display images and we can just revert
    these back to 'grid' subtypes.

    the `childIds` below are referring to any
    arbitrary Blocks that this container...
    contains. In this case, we can refer to

    zoomforth_zf/predefined_blocks/content/predefined_blocks_content.js

    which matches the BlockContent IDs to
    these fields below.

    @TODO: generate the IDs instead of manually setting them.
  */
  {
    blockId: 0,
    columnCount: 3,
    childIds: [4, 5, 6],
    tags: [CATEGORY_TAG_IMAGE_BIO],
    blockType: BLOCK_TYPE_CONTAINER,
    blockSubtype: BLOCK_SUBTYPE_GRID_BIO_STANDARD,
    description: 'Bio Standard'
  },
  {
    blockId: 1,
    columnCount: 4,
    childIds: [7, 8, 9, 10],
    tags: [CATEGORY_TAG_IMAGE_BIO],
    blockType: BLOCK_TYPE_CONTAINER,
    blockSubtype: BLOCK_SUBTYPE_GRID_BIO_ROUNDED,
    description: 'Bio Rounded'
  },
  {
    blockId: 2,
    columnCount: 4,
    childIds: [11, 12],
    tags: [CATEGORY_TAG_IMAGE_BIO],
    blockType: BLOCK_TYPE_CONTAINER,
    blockSubtype: BLOCK_SUBTYPE_GRID_BIO_STANDARD_SINGLE,
    description: 'Bio Single Standard'
  },
  {
    blockId: 3,
    columnCount: 4,
    childIds: [13, 14],
    tags: [CATEGORY_TAG_IMAGE_BIO],
    blockType: BLOCK_TYPE_CONTAINER,
    blockSubtype: BLOCK_SUBTYPE_GRID_BIO_ROUNDED_SINGLE,
    description: 'Bio Single Rounded',

  },
  {
    blockId: 19,
    columnCount: 20,
    childIds: [20, 21],
    tags: [CATEGORY_TAG_IMAGE],
    blockType: BLOCK_TYPE_CONTAINER,
    blockSubtype: BLOCK_SUBTYPE_GRID_IMAGE_AND_TEXT_FULL_BLEED,
    description: 'Image and Text - Full Bleed',
    isFullWidth: true
  },
  {
    blockId: 22,
    columnCount: 20,
    childIds: [23, 24],
    tags: [CATEGORY_TAG_IMAGE],
    blockType: BLOCK_TYPE_CONTAINER,
    blockSubtype: BLOCK_SUBTYPE_GRID_IMAGE_AND_TEXT_BORDERED,
    description: 'Image and Text - Bordered'
  },
  {
    blockId: 25,
    columnCount: 20,
    childIds: [26, 27],
    tags: [CATEGORY_TAG_VIDEO],
    blockType: BLOCK_TYPE_CONTAINER,
    blockSubtype: BLOCK_SUBTYPE_GRID_VIDEO_AND_TEXT_FULL_BLEED,
    description: 'Video and Text - Full Bleed',
    isFullWidth: true
  },
  {
    blockId: 28,
    columnCount: 20,
    childIds: [29, 30],
    tags: [CATEGORY_TAG_VIDEO],
    blockType: BLOCK_TYPE_CONTAINER,
    blockSubtype: BLOCK_SUBTYPE_GRID_VIDEO_AND_TEXT_BORDERED,
    description: 'Video and Text - Bordered'
  }
]);

function generateExistingBlocksContainers(excludeSectionTypes = []) {
  const filteredSectionTypes = SECTION_TYPE_NAMES.filter(sectionType => 
    !excludeSectionTypes.includes(sectionType)
  );

  const existingBlocksObjects = reduce(filteredSectionTypes,
    (blocksObjects, sectionTypeName) => {
      const blockObject = {
        blockType: BLOCK_TYPE_CONTAINER,
        blockSubtype: sectionTypeName === SECTION_TYPE_TILES ? BLOCK_SUBTYPE_GRID : sectionTypeName,
        tags: categoryTagsBySectionType[`${sectionTypeName}`],
        description: (
          SECTION_TYPE_NAMES_TO_SECTION_DESCRIPTION_MAP[
            `${sectionTypeName}`
          ]
        )
      };

      blocksObjects.push(blockObject);

      return blocksObjects;
    }, []
  );

  return existingBlocksObjects;
};

const existingBlocksContainers = Object.freeze([
  ...generateExistingBlocksContainers()
]);

// We freeze this to communicate that this array
// should not be modified, but instead expanded.
const predefinedBlocksContainers = Object.freeze(concat([],
  existingBlocksContainers,
  newBlocksContainers
));

// Function to generate blocks containers with exclusions
export const generatePredefinedBlocksContainers = (excludeSectionTypes = []) => {
  const filteredExistingBlocks = generateExistingBlocksContainers(excludeSectionTypes);
  return Object.freeze(concat([],
    filteredExistingBlocks,
    newBlocksContainers
  ));
};

export default predefinedBlocksContainers;